import { useState } from 'react'
import { sanitizeFileName } from '@/lib/fileNames'

const MIME_EXTENSIONS = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/svg+xml': 'svg',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'audio/mpeg': 'mp3',
    'audio/mp3': 'mp3',
    'audio/wav': 'wav',
    'audio/x-wav': 'wav',
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
    'application/pdf': 'pdf',
}

// Extract a clean extension from a URL pathname, ignoring query strings/hashes.
const getExtensionFromUrl = (url) => {
    try {
        const { pathname } = new URL(url, window.location.origin)
        const lastSegment = pathname.split('/').pop() || ''
        if (lastSegment.includes('.')) {
            return lastSegment.split('.').pop().toLowerCase()
        }
    } catch {
        const cleaned = url.split('?')[0].split('#')[0]
        const lastSegment = cleaned.split('/').pop() || ''
        if (lastSegment.includes('.')) {
            return lastSegment.split('.').pop().toLowerCase()
        }
    }
    return ''
}

export default function useFileDownload() {
    const [downloadingFile, setDownloadingFile] = useState(false)
    const [error, setError] = useState(null)

    const downloadFile = async (url, fileName) => {
        if(!url) return
        return new Promise(async (resolve, reject) => {
            try {
                setDownloadingFile(true)
                setError(null)

                const response = await fetch(url)
                if (!response.ok) throw new Error('Download failed')

                const blob = await response.blob()
                const downloadUrl = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = downloadUrl

                // Resolve the extension from (in order): the URL pathname, then the
                // blob's MIME type. Relying on the browser's <a download> heuristic
                // is unreliable for video, so we always set an explicit extension.
                const extension =
                    getExtensionFromUrl(url) || MIME_EXTENSIONS[blob.type] || ''

                const defaultName = (() => {
                    let fromUrl = ''
                    if (getExtensionFromUrl(url)) {
                        const lastSegment = url.split('?')[0].split('/').pop() || ''
                        try {
                            fromUrl = decodeURIComponent(lastSegment)
                        } catch {
                            // Malformed percent-encoding — keep the raw segment
                            // rather than failing the whole download.
                            fromUrl = lastSegment
                        }
                    }
                    return fromUrl || (extension ? `download.${extension}` : 'download')
                })()

                // Append the resolved extension unless the name already ends with it.
                // We can't rely on `name.includes('.')` because asset names often
                // contain dots (e.g. "seedance 2.0 - bears") without being extensions.
                let finalFileName
                if (fileName) {
                    const alreadyHasExt =
                        extension &&
                        fileName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)
                    finalFileName =
                        extension && !alreadyHasExt
                            ? `${fileName}.${extension}`
                            : fileName
                } else {
                    finalFileName = defaultName
                }
                // Asset names are free text and the url segment is attacker-
                // influenced once decoded, so never hand either to the browser
                // unfiltered. See lib/fileNames.
                link.download = sanitizeFileName(finalFileName, 'download')

                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                window.URL.revokeObjectURL(downloadUrl)
                setDownloadingFile(false)
                resolve(true)
            } catch (err) {
                setError(err.message)
                setDownloadingFile(false)
                reject(err)
            }
        })
    }

    return {
        downloadFile,
        downloadingFile,
        error,
    }
}
